import { useState, useRef } from "react";
import { toast } from "sonner";
import { Camera, ShieldCheck } from "@phosphor-icons/react";
import { api, fileUrl, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    full_name: user.full_name || "",
    date_of_birth: user.date_of_birth || "",
    personal_email: user.personal_email || "",
    phone: user.phone || "",
    mailing_address: {
      street: user.mailing_address?.street || "",
      city: user.mailing_address?.city || "",
      state: user.mailing_address?.state || "",
      zip_code: user.mailing_address?.zip_code || "",
    },
  });
  const [hireDate, setHireDate] = useState("");
  const [hireConfirm, setHireConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const setAddr = (k, v) => setForm({ ...form, mailing_address: { ...form.mailing_address, [k]: v } });

  const needsHireDate = !user.hire_date;
  const hireMismatch = needsHireDate && hireDate && hireConfirm && hireDate !== hireConfirm;
  const hireOk = !needsHireDate || (hireDate && hireDate === hireConfirm);
  const addr = form.mailing_address;
  const hasPhoto = !!(user.photo_file_path || user.avatar_url);
  const complete = form.full_name && form.date_of_birth && form.personal_email && form.phone
    && addr.street && addr.city && addr.state && addr.zip_code && hireOk && hasPhoto;

  const save = async () => {
    setBusy(true);
    try {
      const payload = needsHireDate ? { ...form, hire_date: hireDate } : form;
      await api.put(`/users/${user.id}/profile`, payload);
      toast.success("Profile updated and verified for the next 6 months");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/users/${user.id}/photo`, fd);
      toast.success("Profile photo updated");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const photo = user.photo_file_path ? fileUrl(user.photo_file_path) : user.avatar_url;

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Account</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">My profile</h1>
        {user.needs_profile_verification && (
          <p className="text-sm rounded-md bg-red-50 border border-red-200 text-red-800 px-4 py-3 mt-3" data-testid="profile-locked-msg">
            <strong>The app is locked</strong> until you complete and confirm your profile below. Every field is required — photo, name, date of birth, personal email, phone, mailing address and your hire date (entered twice). Company policy then requires re-confirming every 6 months.
          </p>
        )}
        {user.profile_confirmed_at && !user.needs_profile_verification && (
          <p className="text-sm text-emerald-700 mt-2 flex items-center gap-1.5" data-testid="profile-verified-msg">
            <ShieldCheck size={16} weight="duotone" /> Verified {fmtDate(user.profile_confirmed_at)} — next verification due in 6 months
          </p>
        )}
      </div>

      <div className="bg-card border border-border rounded-md p-6 flex items-center gap-5">
        <Avatar className="h-20 w-20 border border-border">
          <AvatarImage src={photo} alt={user.full_name} />
          <AvatarFallback className="bg-slate-200 text-lg">{user.full_name?.split(" ").map((w) => w[0]).join("").slice(0, 2)}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-medium">Profile photo <span className="text-red-500">*required</span></p>
          <p className="text-xs text-muted-foreground mb-3">A clear photo of you, uploaded by you.</p>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
          <Button data-testid="profile-photo-btn" size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
            <Camera size={15} className="mr-1.5" /> Upload photo
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md p-6 space-y-5">
        <h3 className="font-heading font-semibold tracking-tight">Personal information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Full name</Label><Input data-testid="profile-name-input" className="mt-1.5" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
          <div><Label>Date of birth</Label><Input data-testid="profile-dob-input" type="date" className="mt-1.5" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} /></div>
          <div><Label>Personal email</Label><Input data-testid="profile-personal-email-input" type="email" className="mt-1.5" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} /></div>
          <div><Label>Phone number</Label><Input data-testid="profile-phone-input" className="mt-1.5" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-1">Personal mailing address</h4>
          <p className="text-xs text-muted-foreground mb-3">Must be a physical street address — PO Boxes are not accepted.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label>Street</Label><Input data-testid="profile-street-input" className="mt-1.5" value={form.mailing_address.street} onChange={(e) => setAddr("street", e.target.value)} /></div>
            <div><Label>City</Label><Input data-testid="profile-city-input" className="mt-1.5" value={form.mailing_address.city} onChange={(e) => setAddr("city", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>State</Label><Input data-testid="profile-state-input" className="mt-1.5" value={form.mailing_address.state} onChange={(e) => setAddr("state", e.target.value)} /></div>
              <div><Label>ZIP</Label><Input data-testid="profile-zip-input" className="mt-1.5" value={form.mailing_address.zip_code} onChange={(e) => setAddr("zip_code", e.target.value)} /></div>
            </div>
          </div>
        </div>
        <div>
          <h4 className="text-sm font-semibold mb-1">Employment</h4>
          {needsHireDate ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">Enter your hire date twice to confirm. It sets your annual performance-review anniversary and your work milestones.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Hire date <span className="text-red-500">*</span></Label><Input data-testid="profile-hire-date-input" type="date" className="mt-1.5" value={hireDate} onChange={(e) => setHireDate(e.target.value)} /></div>
                <div><Label>Confirm hire date <span className="text-red-500">*</span></Label><Input data-testid="profile-hire-confirm-input" type="date" className="mt-1.5" value={hireConfirm} onChange={(e) => setHireConfirm(e.target.value)} /></div>
              </div>
              {hireMismatch && (
                <p className="text-xs text-red-600" data-testid="hire-date-mismatch">The two hire dates don't match — please double-check.</p>
              )}
              {hireOk && hireDate && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3.5 py-2.5" data-testid="hire-date-lock-warning">
                  <p className="text-xs text-amber-800">You're confirming your hire date as <strong>{fmtDate(hireDate)}</strong>. Once submitted it is <strong>locked</strong> — only a super admin can change it.</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm" data-testid="hire-date-locked-row">
              <span className="text-muted-foreground">Hire date on file: </span><strong>{fmtDate(user.hire_date)}</strong>
              <span className="block text-xs text-muted-foreground mt-0.5">Locked — contact a super admin if it needs correcting. Your annual review lands on this anniversary.</span>
            </p>
          )}
        </div>
        {!complete && (
          <p className="text-xs text-muted-foreground" data-testid="profile-incomplete-hint">All fields are required before you can verify your profile{!hasPhoto ? " — including a profile photo (upload above)" : ""}.</p>
        )}
        <Button data-testid="profile-save-btn" onClick={save} disabled={busy || !complete} className="bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">
          {busy ? "Saving…" : "Save & verify profile"}
        </Button>
      </div>
    </div>
  );
}
